import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useMotionValue } from "framer-motion";
import { ArrowLeft, ArrowRight, Quote } from "lucide-react";
import {
  PERSON_1,
  PERSON_2,
  PERSON_3,
  PERSON_4,
  PERSON_5,
  PERSON_6,
} from "../constants/ImageConstants";

const testimonials = [
  {
    text: "The Old Hellfire Club Ltd. has been able to grow with confidence thanks in large part to AAIDAN K. I've been able to maintain complete control over the money while concentrating on the creative aspects of the company because to their real-time insights and financial clarity. It's similar to having a financial team that genuinely helps companies become adaptable, astute, and available whenever needed.",
    name: "Peter Crosby",
    title: "Founder",
    company: "The Old Hellfire Club Ltd",
    image: PERSON_1,
  },
  {
    text: "Having AAIDAN K made running Yellow Tree's finances shockingly simple. Their strategic assistance and efficient management of daily administrative duties have freed us up to develop and innovate. They collaborate with you to advance your company, not merely handle the books.",
    name: "Sachin Meru",
    title: "Director",
    company: "Yellow Tree Integration Limited",
    image: PERSON_2,
  },
  {
    text: "Numbers aren't my forte as a creative professional, but with AAIDAN K, they don't have to be. From bookkeeping to taxes, they have made everything simpler while maintaining a humane and individualized approach. I have total faith in them to handle my company's finances.",
    name: "Steve Ash",
    title: "Illustrator & Founder",
    company: "Steve Ash Illustration Ltd",
    image: PERSON_3,
  },
  {
    text: "We have been able to firmly establish our idea on sound financial systems thanks to AAIDAN K. Awakening System Ltd. has greatly benefited from their patient support and strategic views. They pay attention, adjust, and provide us with knowledgeable and considerate guidance.",
    name: "Tracey Ash",
    title: "Founder",
    company: "Awakening System Ltd",
    image: PERSON_5,
  },
  {
    text: "It used to be a yearly stressor for me to deal with self-evaluation before I met AAIDAN K. With clarity and without any jargon, they took over the entire procedure. Everything was stress-free, correct, and submitted on schedule. I feel in charge of my money now.",
    name: "David",
    title: "Sole Trader",
    company: "Self Assessment",
    image: PERSON_4,
  },
  {
    text: "What seemed like a confusing bureaucratic nightmare was transformed into something remarkably seamless by AAIDAN K. Their assistance with my self-evaluation was not only effective but also incredibly considerate. For the first time, I feel in control of my own financial responsibilities.",
    name: "Nathalie Bouziotos",
    title: "",
    company: "Self Assessment",
    image: PERSON_6,
  },
];

const Testimonials = ({ className }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(null);
  const timerRef = useRef(null);

  // Reset timer function
  const resetTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    timerRef.current = setInterval(() => {
      nextTestimonial();
    }, 7000);
  };

  useEffect(() => {
    resetTimer();
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [currentIndex]);

  const nextTestimonial = () => {
    setDirection("right");
    setCurrentIndex((prevIndex) =>
      prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevTestimonial = () => {
    setDirection("left");
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
    resetTimer();
  };

  const handleNext = () => {
    nextTestimonial();
    resetTimer();
  };

  const handlePrev = () => {
    prevTestimonial();
    resetTimer();
  };

  const goToTestimonial = (index) => {
    setDirection(index > currentIndex ? "right" : "left");
    setCurrentIndex(index);
    resetTimer();
  };

  // Swipe handling
  const dragX = useMotionValue(0);
  const SWIPE_THRESHOLD = 50;

  const handleDragEnd = (event, info) => {
    const offset = info.offset.x;
    const velocity = info.velocity.x;

    if (offset > SWIPE_THRESHOLD || velocity > 500) {
      handlePrev();
    } else if (offset < -SWIPE_THRESHOLD || velocity < -500) {
      handleNext();
    }
  };

  const slideVariants = {
    hiddenRight: {
      x: "100%",
      opacity: 0,
    },
    hiddenLeft: {
      x: "-100%",
      opacity: 0,
    },
    visible: {
      x: "0",
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeInOut",
      },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: {
        duration: 0.4,
      },
    },
  };

  return (
    <section
      className={`py-24 bg-gradient-to-br from-gray-900 to-black relative overflow-hidden ${
        className || ""
      }`}
      id="testimonials"
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-gray-700/30"
            style={{
              width: `${Math.random() * 100 + 30}px`,
              height: `${Math.random() * 100 + 30}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -20, 0],
              x: [0, 15, 0],
            }}
            transition={{
              duration: 15 + Math.random() * 20,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-white mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            What Our Clients Say
          </motion.h2>

          <motion.p
            className="text-xl text-gray-300 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Hear from entrepreneurs who transformed their financial operations
          </motion.p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          {/* Desktop Navigation Arrows */}
          <div className="hidden md:flex justify-between items-center absolute top-1/2 -left-16 -right-16 -translate-y-1/2 z-20">
            <button
              onClick={handlePrev}
              className="bg-gray-800 border border-gray-700 rounded-full p-3 shadow-lg hover:bg-gray-700 transition-colors"
              aria-label="Previous testimonial"
            >
              <ArrowLeft className="w-6 h-6 text-gray-300" />
            </button>
            <button
              onClick={handleNext}
              className="bg-gray-800 border border-gray-700 rounded-full p-3 shadow-lg hover:bg-gray-700 transition-colors"
              aria-label="Next testimonial"
            >
              <ArrowRight className="w-6 h-6 text-gray-300" />
            </button>
          </div>

          {/* Testimonial slider with drag support */}
          <motion.div
            className="relative bg-gray-800/60 backdrop-blur-sm border border-gray-700 rounded-2xl shadow-xl overflow-hidden min-h-[400px] cursor-grab active:cursor-grabbing"
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.2}
            onDragEnd={handleDragEnd}
            style={{ x: dragX }}
          >
            <div className="absolute top-0 right-0 p-6 pointer-events-none">
              <Quote className="w-12 h-12 text-red-900/50" />
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                variants={slideVariants}
                initial={direction === "right" ? "hiddenRight" : "hiddenLeft"}
                animate="visible"
                exit="exit"
                className="px-8 py-12 md:px-16 md:py-16"
              >
                <div className="flex flex-col md:flex-row gap-10 items-center">
                  <div className="flex-shrink-0">
                    <div className="relative">
                      <img
                        src={testimonials[currentIndex].image}
                        alt={`${testimonials[currentIndex].name}`}
                        className="w-40 h-40 object-contain"
                        style={{
                          borderRadius: 0,
                          border: "none",
                        }}
                      />
                    </div>
                  </div>

                  <div className="flex-1 text-center md:text-left">
                    <div className="text-gray-300 text-lg md:text-xl leading-relaxed mb-8">
                      "{testimonials[currentIndex].text}"
                    </div>

                    <div>
                      <h4 className="text-xl font-bold text-white">
                        {testimonials[currentIndex].name}
                      </h4>
                      <p className="text-gray-400">
                        {testimonials[currentIndex].title &&
                          `${testimonials[currentIndex].title}, `}
                        {testimonials[currentIndex].company}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </motion.div>

          {/* Dot indicators */}
          {/* <div className="flex justify-center gap-2 mt-6">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToTestimonial(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex
                    ? "bg-red-600 w-8"
                    : "bg-gray-600 hover:bg-gray-500"
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div> */}
        </div>

        {/* Mobile Navigation */}
        <div className="flex justify-center gap-4 mt-8 md:hidden">
          <button
            onClick={handlePrev}
            className="bg-gray-800 border border-gray-700 rounded-xl p-3 shadow-lg hover:bg-gray-700 transition-colors active:scale-95"
            aria-label="Previous testimonial"
          >
            <ArrowLeft className="w-5 h-5 text-gray-300" />
          </button>
          <button
            onClick={handleNext}
            className="bg-gray-800 border border-gray-700 rounded-full p-3 shadow-lg hover:bg-gray-700 transition-colors active:scale-95"
            aria-label="Next testimonial"
          >
            <ArrowRight className="w-5 h-5 text-gray-300" />
          </button>
        </div>
      </div>

      {/* Decorative elements */}
      <motion.div
        className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-red-900/50 blur-xl"
        animate={{
          y: [0, -15, 0],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <motion.div
        className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-red-800/50 blur-xl"
        animate={{
          y: [0, 15, 0],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1,
        }}
      />

      {/* Glitter effect */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-white/10"
            style={{
              width: `${Math.random() * 4 + 1}px`,
              height: `${Math.random() * 4 + 1}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
            }}
            animate={{
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 1.5 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 2,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>
    </section>
  );
};

export default Testimonials;
